import { Box, CircularProgress, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { Check } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'
import { AuthorizedButton } from '~/components/communication/AuthorizedButton'
import { StoreDetailsLayout } from '~/components/routes/stores/StoreDetailsLayout'
import {
  PendingInventoryTab,
  type PendingInventoryData,
} from '~/components/routes/stores/inventories/PendingInventoryTab'
import { STORE_TABS, STORE_TAB_PERMISSIONS, type StoreTab } from '~/components/routes/stores/tabs'
import { withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/stores/$storeId/inventory')({
  staticData: {
    permission: STORE_TAB_PERMISSIONS.inventory,
  },
  component: StoreCurrentInventoryTab,
  params: {
    parse: z.object({ storeId: z.coerce.number() }).parse,
  },
})
withRoutePermissions(Route)

function StoreCurrentInventoryTab() {
  const navigate = Route.useNavigate()
  const { storeId } = Route.useParams()

  // Les actions de validation vivent dans l'entête, mais leur état appartient au panneau.
  const [inventory, setInventory] = useState<PendingInventoryData | null>(null)

  const goToTab = (tab: StoreTab) => {
    const target = STORE_TABS.find((entry) => entry.value === tab)
    if (target) navigate({ to: target.to, params: { storeId } })
  }

  return (
    <StoreDetailsLayout
      storeId={storeId}
      tab="inventory"
      headerActions={
        inventory && (
          <>
            <Typography sx={{ marginRight: 3 }}>Validation Admin :</Typography>
            <AuthorizedButton
              permission={['delete', 'stores.current-inventory.inventory']}
              legacyRights={['SUPER_ADMIN']}
              variant="outlined"
              color="error"
              sx={{ bgcolor: 'white', height: '36px', marginRight: '8px' }}
              onClick={inventory.handleCancel}
              disabled={inventory.isPending}
            >
              Annuler l'inventaire
            </AuthorizedButton>
            <AuthorizedButton
              permission={['validate-inventory', 'stores.current-inventory.validation']}
              legacyRights={['SUPER_ADMIN']}
              variant="contained"
              sx={{ height: '36px' }}
              startIcon={
                inventory.isPending ? <CircularProgress size={16} color="inherit" /> : <Check />
              }
              onClick={inventory.handleValidate}
              disabled={inventory.isPending}
            >
              {inventory.isPending ? 'Validation…' : "Valider l'inventaire"}
            </AuthorizedButton>
          </>
        )
      }
    >
      <Box sx={{ marginTop: '16px' }}>
        <PendingInventoryTab
          shopId={storeId}
          onInventoryDataChange={setInventory}
          setTab={goToTab}
        />
      </Box>
    </StoreDetailsLayout>
  )
}
